import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useStore } from '../StateProvider';
import DayEventsModal from './DayEventsModal';
import { getList } from '../../functions/api';
import { getlocal } from '../../functions/localstorage';
import { TbHammer } from "react-icons/tb";
import { FaBalanceScale } from "react-icons/fa";
import { PiTrademarkDuotone } from "react-icons/pi";

const CalendarPage = () => {
  const store = useStore();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDayEvents, setSelectedDayEvents] = useState(null);
  const [selectedDayDate, setSelectedDayDate] = useState(null);
  const [scheduledTasks, setScheduledTasks] = useState([]);
  const [showScheduleOverlay, setShowScheduleOverlay] = useState(false);
  const [showAddTaskOptions, setShowAddTaskOptions] = useState(false); // New state for add task dropdown
  const [view, setView] = useState('month'); // Default view to month
  const [filterTypeId, setFilterTypeId] = useState(null); // New state for type filtering
  const [showFilter, setShowFilter] = useState(false); // State for filter dropdown visibility
  const [monthStatusSummary, setMonthStatusSummary] = useState({}); // New state for month status summary
  const filterRef = useRef(); // Ref for filter dropdown to handle outside clicks

  const currentUser = getlocal("system")?.user || {};
  const tableURL = "/subtask/getsubtasklist";

  useEffect(() => {
    store.setmodule("dashboard");
    store.setheader("");
    store.stopLoading();
  }, []);

  // Dynamically get API URL based on user role
  const getDynamicTableURL = useCallback(() => {
    return currentUser.role === 1 ? tableURL : `${tableURL}/${currentUser.id}`;
  }, [currentUser.id, currentUser.role]);

  // Effect to close filter dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target)) {
        setShowFilter(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetches events based on current month and applied filter
  const fetchMonthEvents = useCallback(async () => {
    setLoading(true);
    const year = currentDate.getFullYear();
    const month = (currentDate.getMonth() + 1).toString().padStart(2, '0');

    try {
      const res = await getList({
        listUrl: getDynamicTableURL(),
        field: "dueDate",
        search: '', // Keep search empty to fetch all for the month and then filter
        sort: "asc",
        count: 500000,
        store: store,
      });

      let allTasks = res?.data?.list || [];

      // Apply type filter if set
      if (filterTypeId) {
        allTasks = allTasks.filter(task => task.type_id === filterTypeId);
      }

      // Filter tasks to only include those in the current month and unique ones
      const filteredTasks = allTasks.filter((task, index, self) => {
        const taskDate = new Date(task.dueDate);
        return (
          taskDate.getFullYear() === year &&
          taskDate.getMonth() === currentDate.getMonth() &&
          index === self.findIndex(t => t.id === task.id) // Ensure unique tasks by ID
        );
      });

      const formattedEvents = filteredTasks.map(task => ({
        id: task.id,
        title: task.subtask_title,
        date: task.dueDate.split('T')[0],
        type: task.type, // 'meeting', 'task', 'deadline' - used for styling
        project: task.projectName,
        assignedTo: task.assigned_to_username || '--',
        status: task.status || '--',
        type_id: task.type_id // Used for navigation in handleViewSpecificEventDetails
      }));

      setEvents(formattedEvents);

      const summary = {
        draft: formattedEvents.some(event => event.status?.toLowerCase() === 'draft'),
        draftOverdue: formattedEvents.some(
          event => event.status?.toLowerCase() === 'draft' && new Date(event.date) < new Date()
        ),
        completed: formattedEvents.some(event => event.status?.toLowerCase() === 'completed'),
        waitingForApproval: formattedEvents.some(event => event.status?.toLowerCase() === 'waitingforapproval'),
        inProgress: formattedEvents.some(event => event.status?.toLowerCase() === 'inprogress'),
        inProgressOverdue: formattedEvents.some(
          event => event.status?.toLowerCase() === 'inprogress' && new Date(event.date) < new Date()
        ),
        hold: formattedEvents.some(event => event.status?.toLowerCase() === 'hold'),
        rejected: formattedEvents.some(event => event.status?.toLowerCase() === 'rejected'),
      };
      setMonthStatusSummary(summary);
    } catch (error) {
      console.error("Error fetching events:", error);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [currentDate, store, filterTypeId, getDynamicTableURL]); // Added getDynamicTableURL to dependencies

  useEffect(() => {
    fetchMonthEvents();
  }, [fetchMonthEvents]);



  const handleAddTaskWithType = (typeId) => {
    if (selectedDayDate) {
      if (typeId === 11) {
        store.removemodule("nonlitigation_task");
        store.navTo(`/demo/tasks/create?dueDate=${selectedDayDate}`);
      } else if (typeId === 12) {
        store.removemodule("litigation_task");
        store.navTo(`/demo/litigation/tasks/create?dueDate=${selectedDayDate}`);
      } else {
        store.removemodule("trademark_task");
        store.navTo(`/demo/trademark/tasks/create?dueDate=${selectedDayDate}`);
      }
      setShowAddTaskOptions(false);
      closeDayEventsModal();
    }
  };

  const getDaysInMonth = (date) => new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  const getFirstDayOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1).getDay();

  const prevMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));

  // Opens the DayEventsModal and fetches all events for that specific day
  const openDayEventsModal = async (dateString, eventsForDay) => {
    setSelectedDayDate(dateString);
    setSelectedDayEvents(eventsForDay); // Initial events passed, but we'll fetch comprehensive list
    try {
      const res = await getList({
        listUrl: getDynamicTableURL(),
        field: "dueDate",
        search: dateString, // Search specifically for the selected date
        sort: "asc",
        count: 500000,
        store: store,
      });
      // Filter by type_id if a filter is active
      const tasksForDay = filterTypeId ? res?.data?.list.filter(task => task.type_id === filterTypeId) : res?.data?.list;
      setScheduledTasks(tasksForDay || []);
    } catch (error) {
      console.error("Error fetching scheduled tasks for day:", error);
      setScheduledTasks([]);
    } finally {
      setShowScheduleOverlay(true); // Always show overlay even if no tasks found or error
    }
  };

  const closeDayEventsModal = () => {
    setSelectedDayDate(null);
    setSelectedDayEvents(null);
    setShowScheduleOverlay(false);
    setScheduledTasks([]);
  };

  // Handles navigation to specific event details page
  const handleViewSpecificEventDetails = (eventId, type_id) => {
    const URLPRIFIX = "demo";
    const tablename = "tasks";

    // Assuming store.getprops("view") controls some global view setting
    // This logic determines the correct path based on type_id
    if (store.getprops("view")) {
      if (type_id === 11) { // Non-Litigation
        store.navTo(`/${URLPRIFIX}/${tablename}/view/${eventId}`);
      } else if (type_id === 12) { // Litigation
        store.navTo(`/${URLPRIFIX}/litigation/${tablename}/view/${eventId}`);
      } else if (type_id === 13) { // Trademark
        store.navTo(`/${URLPRIFIX}/trademark/${tablename}/view/${eventId}`);
      } else {
        // Fallback or default case if type_id doesn't match
        store.navTo(`/${URLPRIFIX}/${tablename}/view/${eventId}`);
      }
    } else {
      // If store.getprops("view") is false or undefined
      store.navTo(`/${URLPRIFIX}/${tablename}/view/${eventId}`);
    }
    closeDayEventsModal(); // Close the modal after navigating
  };

  // Handles the main "Add Task" button click (might be deprecated by type-specific add)
  const handleAddTask = () => {
    if (selectedDayDate) {
      store.navTo(`/demo/tasks/add?dueDate=${selectedDayDate}`);
      closeDayEventsModal();
    }
  };

  // Handles filtering calendar events by type
  const handleTypeFilter = (type_id) => {
    setFilterTypeId(type_id);
    setShowFilter(false); // Close filter dropdown after selection
  };

  // Renders the days for the month view
  const renderDays = () => {
    const days = [];
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate);
    const MAX_EVENTS_TO_SHOW = 2; // Max events to show in a day cell

    // Add empty divs for leading blank days in the first week
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="p-2 border border-[#E0E0E0] bg-[#F9F9F9] rounded-lg"></div>);
    }

    const now = new Date();
    now.setHours(0, 0, 0, 0); // Normalize 'now' to start of day for accurate overdue check

    for (let i = 1; i <= daysInMonth; i++) {
      const day = i;
      const fullDate = `${currentDate.getFullYear()}-${(currentDate.getMonth() + 1).toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;

      // Filter events for this specific day (already filtered by type at fetch level)
      const dayEvents = events.filter(event => event.date === fullDate);

      const isToday = new Date().toDateString() === new Date(currentDate.getFullYear(), currentDate.getMonth(), day).toDateString();

      days.push(
        <div
          key={day}
          onClick={() => openDayEventsModal(fullDate, dayEvents)}
          className={`p-2 border border-[#D1D5DB] rounded-lg relative min-h-[100px] cursor-pointer shadow-sm transition-transform hover:scale-[1.015] ${isToday ? 'bg-[#E8F0FE]' : 'bg-[#FFFFFF]'} overflow-hidden`}
        >
          <span className={`font-bold text-base ${isToday ? 'text-[#1A73E8]' : 'text-[#374151]'}`}>{day}</span>
          <div className="mt-2 flex flex-col space-y-1 overflow-hidden">
            {dayEvents.length > 0 ? (
              <>
                {dayEvents.slice(0, MAX_EVENTS_TO_SHOW).map(event => {
                  const eventDueDate = new Date(event.date);
                  eventDueDate.setHours(0, 0, 0, 0); // Normalize event due date

                  // Determine status-based styling
                  let bgColorClass = 'bg-[#E6F4EA] text-[#188038]'; // Default green for completed/other

                  const status = event.status?.toLowerCase();
                  const isOverdue = eventDueDate < now;

                  if ((status === 'draft' || status === 'inprogress') && isOverdue) {
                    bgColorClass = 'bg-[#f5b7b1] text-[#374151]'; // Red for overdue draft/inprogress
                  } else if (status === 'draft') {
                    bgColorClass = 'bg-[#d6eaf8] text-[#21618c]'; // Blue for draft
                  } else if (status === 'inprogress') {
                    bgColorClass = 'bg-[#f4ecf7] text-[#212f3d]'; // Purple for inprogress
                  } else if (status === 'waitingforapproval') {
                    bgColorClass = 'bg-[#f9e79f] text-[#b7950b]';
                  } else if (status === 'hold') {
                    bgColorClass = 'bg-[#fae7b1] text-[#916a03]';
                  } else if (status === 'rejected') {
                    bgColorClass = 'bg-[#ffcccb] text-[#cc0000]';
                  }


                  return (
                    <div
                      key={event.id}
                      className={`text-xs px-2 py-1 rounded-md truncate ${bgColorClass}`}
                      title={event.title}
                    >
                      {event.title}
                    </div>
                  );
                })}
                {dayEvents.length > MAX_EVENTS_TO_SHOW && (
                  <div className="text-xs text-center text-[#5F6368] mt-1 py-1 px-2 rounded-md bg-[#F1F3F4] hover:bg-[#E8EAED]">
                    +{dayEvents.length - MAX_EVENTS_TO_SHOW} more
                  </div>
                )}
              </>
            ) : (
              <span className="text-xs text-[#9CA3AF]">No tasks</span>
            )}
          </div>
        </div>
      );
    }
    return days;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA]">
        <div className="p-6 bg-white rounded-xl shadow-xl flex flex-col items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-t-[#1A73E8] border-gray-300 mb-4"></div>
          <p className="text-gray-700 text-lg">Loading Calendar...</p>
        </div>
      </div>
    );
  }

  const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  return (
    <div className="min-h-screen bg-[#F1F3F4] p-4 font-sans">
      <div className="max-w-7xl mx-auto">
        <header className="flex justify-between items-center mb-4">
          <div className="flex flex-col">
            <h1 className="text-3xl font-bold text-[#202124]">{monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}</h1>
            <div className="flex ml-4 space-x-2">
              {monthStatusSummary.draftOverdue ? (
                <div className="relative group">
                  <span className="w-3 h-3 bg-[#f5b7b1] rounded-full inline-block cursor-pointer border border-[#cc0000]"></span>
                  <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 px-3 py-1 bg-gray-800 text-white text-xs rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                    Draft Overdue
                  </div>
                </div>
              ) : monthStatusSummary.draft && (
                <div className="relative group">
                  <span className="w-3 h-3 bg-[#d6eaf8] rounded-full inline-block cursor-pointer border border-[#21618c]"></span>
                  <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 px-3 py-1 bg-gray-800 text-white text-xs rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                    Draft
                  </div>
                </div>
              )}


              {monthStatusSummary.completed && (
                <div className="relative group">
                  <span className="w-3 h-3 bg-[#E6F4EA] rounded-full inline-block cursor-pointer border border-[#188038]"></span>
                  <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 px-3 py-1 bg-gray-800 text-white text-xs rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">Completed</div>
                </div>
              )}
              {monthStatusSummary.waitingForApproval && (
                <div className="relative group">
                  <span className="w-3 h-3 bg-[#f9e79f] rounded-full inline-block cursor-pointer border border-[#b7950b]"></span>
                  <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 px-3 py-1 bg-gray-800 text-white text-xs rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">Waiting for Approval</div>
                </div>
              )}
              {monthStatusSummary.inProgressOverdue ? (
                <div className="relative group">
                  <span className="w-3 h-3 bg-[#f5b7b1] rounded-full inline-block cursor-pointer border border-[#e74c3c]"></span>
                  <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 px-3 py-1 bg-gray-800 text-white text-xs rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                    In Progress Overdue
                  </div>
                </div>
              ) : monthStatusSummary.inProgress && (
                <div className="relative group">
                  <span className="w-3 h-3 bg-[#e0d4fd] rounded-full inline-block cursor-pointer border border-[#5e3db4]"></span>
                  <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 px-3 py-1 bg-gray-800 text-white text-xs rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                    In Progress
                  </div>
                </div>
              )}

              {monthStatusSummary.hold && (
                <div className="relative group">
                  <span className="w-3 h-3 bg-[#fae7b1] rounded-full inline-block cursor-pointer border border-[#e67e22]"></span>
                  <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 px-3 py-1 bg-gray-800 text-white text-xs rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">On Hold</div>
                </div>
              )}
              {monthStatusSummary.rejected && (
                <div className="relative group">
                  <span className="w-3 h-3 bg-[#ffcccb] rounded-full inline-block cursor-pointer border border-[#cc0000]"></span>
                  <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 px-3 py-1 bg-gray-800 text-white text-xs rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">Rejected</div>
                </div>
              )}
            </div>
            {filterTypeId && (
              <p className="text-sm text-[#5F6368] mt-1 ml-1">
                {filterTypeId === 11 ? 'Non-Litigation' : filterTypeId === 12 ? 'Litigation' : 'Trademark'} Tasks
              </p>
            )}
          </div>

          <div className="flex items-center space-x-3">
            {/* Prev/Next Month Buttons */}
            <button onClick={prevMonth} className="p-2 bg-white rounded-full shadow-md hover:bg-[#E8EAED]">
              <svg className="w-5 h-5 text-[#5F6368]" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd"></path></svg>
            </button>
            <button onClick={nextMonth} className="p-2 bg-white rounded-full shadow-md hover:bg-[#E8EAED]">
              <svg className="w-5 h-5 text-[#5F6368]" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd"></path></svg>
            </button>

            {/* Filter Dropdown */}
            <div ref={filterRef} className="relative">
              <button onClick={() => setShowFilter(prev => !prev)} className="p-2 bg-white rounded-full shadow-md hover:bg-[#E8EAED]">
                <svg className="w-5 h-5 text-[#5F6368]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2a1 1 0 01-.293.707L15 13.414V19a1 1 0 01-1.447.894l-4-2A1 1 0 019 17v-3.586L3.293 6.707A1 1 0 013 6V4z" />
                </svg>
                {filterTypeId && <span className="absolute top-1 right-1 w-2 h-2 bg-[#1A73E8] rounded-full"></span>}
              </button>
              {showFilter && (
                <div
                  className="absolute right-0 mt-2 w-48 bg-[#FFFFFF] border rounded-lg shadow-md z-10"
                  style={{
                    backgroundColor: '#ffffff',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    border: '1px solid #0000001a',
                  }}
                >
                  <ul className="py-1 text-sm" style={{ color: '#374151' }}>
                    <li
                      className="flex items-center gap-2 px-4 py-2 cursor-pointer hover:bg-[#F1F3F4]"
                      onClick={() => handleTypeFilter(null)}
                    >
                      <span style={{ color: '#F59E0B' }}>📂</span>
                      <span>All Types</span>
                    </li>
                    <li
                      className="flex items-center gap-2 px-4 py-2 cursor-pointer hover:bg-[#F1F3F4]"
                      onClick={() => handleTypeFilter(11)}
                    >
                      <span style={{ color: '#1E3A8A' }}>
                        <FaBalanceScale />
                      </span>
                      <span>Non-Litigation</span>
                    </li>
                    <li
                      className="flex items-center gap-2 px-4 py-2 cursor-pointer hover:bg-[#F1F3F4]"
                      onClick={() => handleTypeFilter(12)}
                    >
                      <span style={{ color: '#1E3A8A' }}>
                        <TbHammer />
                      </span>
                      <span>Litigation</span>
                    </li>
                    <li
                      className="flex items-center gap-2 px-4 py-2 cursor-pointer hover:bg-[#F1F3F4]"
                      onClick={() => handleTypeFilter(13)}
                    >
                      <span style={{ color: '#1E3A8A' }}>
                        <PiTrademarkDuotone />
                      </span>
                      <span>TradeMark</span>
                    </li>
                  </ul>
                  {filterTypeId && ( // Only show clear filter if a filter is active
                    <div className="border-t mt-2 pt-2" style={{ borderColor: '#E5E7EB' }}>
                      <button
                        onClick={() => handleTypeFilter(null)}
                        className="block text-sm px-4 py-2 w-full text-left font-medium hover:bg-[#F1F3F4]"
                        style={{ color: '#DC2626' }}
                      >
                        Clear Filter
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* View Switcher */}
            <div className="flex space-x-2 bg-white rounded-lg p-1 shadow-md">
              <button
                onClick={() => setView('month')}
                className={`px-4 py-2 rounded-md text-sm font-medium ${view === 'month' ? 'bg-tertiary text-white' : 'text-gray-700 hover:bg-gray-100'}`}
              >
                Month
              </button>
              <button
                onClick={() => setView('week')}
                className={`px-4 py-2 rounded-md text-sm font-medium ${view === 'week' ? 'bg-tertiary text-white' : 'text-gray-700 hover:bg-gray-100'}`}
              >
                Week
              </button>
              <button
                onClick={() => setView('day')}
                className={`px-4 py-2 rounded-md text-sm font-medium ${view === 'day' ? 'bg-tertiary text-white' : 'text-gray-700 hover:bg-gray-100'}`}
              >
                Day
              </button>
            </div>

            {selectedDayDate && (
              <div className="relative">
                <button
                  onClick={() => setShowAddTaskOptions(prev => !prev)}
                  className="px-4 py-2 bg-[#1A73E8] text-[#FFFFFF] rounded-lg shadow hover:bg-[#1769D1] text-sm"
                >
                  Add Task
                </button>
                {showAddTaskOptions && (
                  <div className="absolute right-0 mt-2 w-48 bg-[#FFFFFF] border rounded-lg shadow-md z-10">
                    <button
                      onClick={() => handleAddTaskWithType(11)} // Non-Litigation
                      className="w-full text-sm text-left px-4 py-2 hover:bg-[#F1F3F4]"
                    >
                      <span className="flex items-center gap-2 text-[#1E3A8A]">
                        <FaBalanceScale className="text-lg" />
                        Non-Litigation
                      </span>
                    </button>
                    <button
                      onClick={() => handleAddTaskWithType(12)} // Litigation
                      className="w-full text-sm text-left px-4 py-2 hover:bg-[#F1F3F4]"
                    >
                      <span className="flex items-center gap-2 text-[#1E3A8A]">
                        <TbHammer className="text-lg" />
                        Litigation
                      </span>
                    </button>
                    <button
                      onClick={() => handleAddTaskWithType(13)} // Trademark
                      className="w-full text-sm text-left px-4 py-2 hover:bg-[#F1F3F4]"
                    >
                      <span className="flex items-center gap-2 text-[#1E3A8A]">
                        <PiTrademarkDuotone className="text-lg" />
                        Trademark
                      </span>
                    </button>
                  </div>
                )}
              </div>
            )}

          </div>
        </header>

        {/* Calendar/View Content */}
        {
          view === 'month' && (
            <div className="bg-white p-4 rounded-xl shadow-lg">
              <div className="grid grid-cols-7 gap-px bg-[#E0E0E0] text-sm text-[#3C4043] font-medium">
                {dayNames.map(day => (
                  <div key={day} className="text-center py-2 bg-[#F8F9FA]">{day}</div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-px bg-[#E0E0E0]">
                {renderDays()}
              </div>
            </div>
          )
        }

        {
          view === 'week' && (
            <div className="bg-white p-6 rounded-xl shadow-lg">
              {(() => {
                const temp1 = new Date(currentDate);
                const start = new Date(temp1.setDate(temp1.getDate() - temp1.getDay()));
                const temp2 = new Date(start);
                const end = new Date(temp2.setDate(temp2.getDate() + 6));
                return (
                  <h3 className="text-xl font-bold text-gray-800 mb-4">
                    Week View: {start.toDateString()} - {end.toDateString()}
                  </h3>
                );
              })()}
              <p className="text-gray-600 mb-4">This section displays tasks for the current week.</p>
              <ul className="mt-4 space-y-2">
                {events.filter(e => {
                  const eventDate = new Date(e.date);
                  eventDate.setHours(0, 0, 0, 0); // Normalize event date to start of day

                  const temp1 = new Date(currentDate);
                  const startOfWeek = new Date(temp1.setDate(temp1.getDate() - temp1.getDay()));
                  startOfWeek.setHours(0, 0, 0, 0); // Normalize start of week

                  const temp2 = new Date(startOfWeek);
                  const endOfWeek = new Date(temp2.setDate(temp2.getDate() + 6));
                  endOfWeek.setHours(23, 59, 59, 999); // Set to end of day

                  return eventDate >= startOfWeek && eventDate <= endOfWeek;
                }).sort((a, b) => new Date(a.date) - new Date(b.date)) // Sort events by date
                  .map(event => (
                    <li
                      key={event.id}
                      onClick={() => openDayEventsModal(event.date, events.filter(e => e.date === event.date))}
                      className={`p-3 rounded-lg shadow-sm cursor-pointer transition-transform transform hover:scale-[1.01]
                      ${event.status?.toLowerCase() === 'inprogress' ? 'bg-[#f5b7b1] border-l-4 border-[#e74c3c]' :
                          event.status?.toLowerCase() === 'draft' ? 'bg-[#d6eaf8] border-l-4 border-[#3498db]' :
                            event.status?.toLowerCase() === 'waitingforapproval' ? 'bg-[#f9e79f] border-l-4 border-[#f1c40f]' :
                              event.status?.toLowerCase() === 'hold' ? 'bg-[#fae7b1] border-l-4 border-[#e67e22]' :
                                event.status?.toLowerCase() === 'rejected' ? 'bg-[#ffcccb] border-l-4 border-[#cc0000]' :
                                  'bg-[#E6F4EA] border-l-4 border-[#27ae60]' // Default green for completed/other
                        }`}
                      title={`${event.title} (Click to view all events for this day)`}
                    >
                      <p className="font-semibold text-gray-800">{event.title}</p>
                      <p className="text-sm text-gray-600">Date: {event.date} | Project: {event.project} | Status: {event.status}</p>
                    </li>
                  ))}
              </ul>
              {events.filter(e => {
                const eventDate = new Date(e.date);
                eventDate.setHours(0, 0, 0, 0);

                const temp1 = new Date(currentDate);
                const startOfWeek = new Date(temp1.setDate(temp1.getDate() - temp1.getDay()));
                startOfWeek.setHours(0, 0, 0, 0);

                const temp2 = new Date(startOfWeek);
                const endOfWeek = new Date(temp2.setDate(temp2.getDate() + 6));
                endOfWeek.setHours(23, 59, 59, 999);
                return eventDate >= startOfWeek && eventDate <= endOfWeek;
              }).length === 0 && (
                  <p className="text-center text-gray-500 mt-4">No events scheduled for this week.</p>
                )}
            </div>
          )
        }

        {
          view === 'day' && (
            <div className="bg-white p-6 rounded-xl shadow-lg">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Day View: {currentDate.toDateString()}</h3>
              <p className="text-gray-600 mb-4">This section displays a detailed timeline for the selected day's events.</p>
              <ul className="mt-4 space-y-2">
                {events.filter(e => {
                  const eventDate = new Date(e.date);
                  eventDate.setHours(0, 0, 0, 0); // Normalize event date

                  const currentDay = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate());
                  currentDay.setHours(0, 0, 0, 0); // Normalize current day

                  return eventDate.toDateString() === currentDay.toDateString();
                }).sort((a, b) => new Date(a.date) - new Date(b.date)) // Sort events by date
                  .map(event => {
                    const eventDueDate = new Date(event.date);
                    eventDueDate.setHours(0, 0, 0, 0);
                    const now = new Date();
                    now.setHours(0, 0, 0, 0);

                    let bgColorClass = 'bg-gray-100'; // Default
                    if (event.status?.toLowerCase() === 'inprogress' && eventDueDate < now) {
                      bgColorClass = 'bg-[#f5b7b1]'; // Overdue In Progress
                    } else if (event.status?.toLowerCase() === 'draft') {
                      bgColorClass = 'bg-[#d6eaf8]';
                    } else if (event.status?.toLowerCase() === 'waitingforapproval') {
                      bgColorClass = 'bg-[#f9e79f]';
                    } else if (event.status?.toLowerCase() === 'hold') {
                      bgColorClass = 'bg-[#fae7b1]';
                    } else if (event.status?.toLowerCase() === 'rejected') {
                      bgColorClass = 'bg-[#ffcccb]';
                    } else if (event.status?.toLowerCase() === 'completed') {
                      bgColorClass = 'bg-[#E6F4EA]'; // Example for completed
                    }

                    return (
                      <li
                        key={event.id}
                        onClick={() => handleViewSpecificEventDetails(event.id, event.type_id)}
                        className={`p-3 rounded-md cursor-pointer hover:bg-gray-200 transition ${bgColorClass}`}
                      >
                        <div className="font-semibold text-gray-800">{event.title}</div>
                        <div className="text-sm text-gray-600">
                          Project: {event.project} | Assigned To: {event.assignedTo} | Status: {event.status}
                        </div>
                      </li>
                    );
                  })}
              </ul>
              {events.filter(e => {
                const eventDate = new Date(e.date);
                eventDate.setHours(0, 0, 0, 0);
                const currentDay = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate());
                currentDay.setHours(0, 0, 0, 0);
                return eventDate.toDateString() === currentDay.toDateString();
              }).length === 0 && (
                  <p className="text-center text-gray-500 mt-4">No events scheduled for this day.</p>
                )}
            </div>
          )
        }
      </div >

      {/* Day Events Modal */}
      {
        showScheduleOverlay && (
          <DayEventsModal
            date={selectedDayDate}
            dayEvents={selectedDayEvents}
            onClose={closeDayEventsModal}
            onViewEventDetails={(eventId, type_id) => handleViewSpecificEventDetails(eventId, type_id)}
            // Pass scheduledTasks to the modal if it needs to display the comprehensive list fetched inside openDayEventsModal
            scheduledTasks={scheduledTasks}
          />
        )
      }
    </div >
  );
};

export default CalendarPage;